import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowUp,
  Loader2,
  MessageSquarePlus,
  Settings2,
  Plug,
  Sparkles,
  Copy,
  Check,
  AlertTriangle,
  Scissors,
  LogOut,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { rateAgentChat, getAgent, startAgentMessage, waitAgentReply, getAgentChat, accessMe, accessLogout } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { MarkdownMessage } from "@/components/MarkdownMessage";
import AgentAccessGate, { AgentInactiveCard, AgentNotMemberCard, accessFlagKey } from "@/components/access/AgentAccessGate";

// End-user messages for the agent states of step 4a (the backend codes are stable; the text is ours).
const BLOCK_MESSAGES = {
  llm_blocked:
    "El asistente no está disponible en este momento: su empresa alcanzó el límite mensual de uso de inteligencia artificial o no tiene una clave activa. Por favor, comuníquese con el responsable de su empresa.",
  company_inactive:
    "Este asistente está desactivado porque la empresa fue desactivada en Inviktus. Por favor, comuníquese con el responsable de su empresa o con Inviktus.",
  agent_unassigned:
    "Este asistente todavía no está habilitado porque no está asociado a ninguna empresa. Por favor, comuníquese con Inviktus para activarlo.",
};
const STATIC_BLOCKS = ["company_inactive", "agent_unassigned"];
const TRUNCATION_RE = /\n*\(Respuesta acortada: se alcanzó el límite mensual[^)]*\)\s*$/;

// step 5b: the agent asks for a credential again (session closed, expired or membership removed)
const ACCESS_CODES = ["access_required", "not_member"];

const blockFromError = (e) => {
  const code = e?.response?.data?.code;
  return code && BLOCK_MESSAGES[code] ? { code, message: BLOCK_MESSAGES[code] } : null;
};

export default function AgentChat() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const chatId = params.get("chat") || null;

  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [resolved, setResolved] = useState(null); // Fix 8: «¿Le resolvió esto?»
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);
  const [blocked, setBlocked] = useState(null);
  // step 5b: minimal agent when GET /agents/{slug} answers access_required (access screen) and the
  // e-mail of the collaborator session (to offer "Cerrar sesión"), if any.
  const [gate, setGate] = useState(null);
  const [collab, setCollab] = useState(null);
  const { user } = useAuth();
  const isStaff = ["admin", "consultor"].includes(user?.role); // Fix 7 (N17): "Editar" only for staff
  const [reloadKey, setReloadKey] = useState(0);

  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const { agent: a } = await getAgent(slug);
        if (cancelled) return;
        if (a?.access_required) {
          // real company agent without a valid credential: access screen (demo agents never get here)
          setGate(a);
          setAgent(null);
          setCollab(null);
          if (a.active === false) {
            const code = a.inactive_reason === "company_inactive" ? "company_inactive" : "agent_unassigned";
            setBlocked({ code, message: BLOCK_MESSAGES[code] });
          }
          return;
        }
        setGate(null);
        setAgent(a);
        const flag = a?.company_slug ? localStorage.getItem(accessFlagKey(a.company_slug)) : null;
        if (flag) {
          accessMe(slug)
            .then((me) => !cancelled && setCollab(me.agent_allowed ? me.email : null))
            .catch(() => {
              localStorage.removeItem(accessFlagKey(a.company_slug));
              if (!cancelled) setCollab(null);
            });
        }
        if (a && a.active === false) {
          const code = a.inactive_reason === "company_inactive" ? "company_inactive" : "agent_unassigned";
          setBlocked({ code, message: BLOCK_MESSAGES[code] });
        }
        if (chatId) {
          const c = await getAgentChat(slug, chatId);
          if (!cancelled) { setMessages(c.messages || []); setResolved(c.resolved ?? null); }
          if (!cancelled && c.pending) awaitReply(chatId);
        }
      } catch {
        toast.error("Agente no encontrado.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, reloadKey]);

  const reloadAgent = () => setReloadKey((k) => k + 1);

  const onAccessLost = (e) => {
    const code = e?.response?.data?.code;
    if (!ACCESS_CODES.includes(code)) return false;
    setMessages([]);
    setParams({}, { replace: true });
    reloadAgent();
    return true;
  };

  const logoutCollaborator = async () => {
    try {
      await accessLogout(slug);
    } catch {
      // already closed on the server: the local state is cleared anyway
    }
    if (agent?.company_slug) localStorage.removeItem(accessFlagKey(agent.company_slug));
    setCollab(null);
    setMessages([]);
    setParams({}, { replace: true });
    toast.success("Sesión cerrada.");
    reloadAgent();
  };

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const send = async (textOverride, starterId) => {
    const text = (textOverride ?? input).trim();
    if (!text || sending) return;
    setSending(true);
    const optimistic = [
      ...messages,
      { role: "user", content: text, ts: new Date().toISOString() },
    ];
    setMessages(optimistic);
    setInput("");
    let started;
    try {
      started = await startAgentMessage(slug, text, chatId, starterId);
      if (!chatId) setParams({ chat: started.chat_id }, { replace: true });
    } catch (e) {
      if (onAccessLost(e)) {
        setSending(false);
        return;
      }
      const info = blockFromError(e);
      if (info) setBlocked(info);
      toast.error(info?.message || e?.response?.data?.detail || "Error enviando el mensaje.", { duration: 10000 });
      setMessages(messages);
      setInput(text);
      setSending(false);
      return;
    }
    if (!(await awaitReply(started.chat_id, messages))) setInput(text);
  };

  // Polls until the background answer is ready; on failure the server drops the user turn, so the UI does too.
  const awaitReply = async (id, rollback) => {
    setSending(true);
    try {
      const reply = await waitAgentReply(slug, id);
      if (reply) setMessages((prev) => [...prev, reply]);
      if (blocked?.code === "llm_blocked") setBlocked(null);
      return true;
    } catch (e) {
      if (onAccessLost(e)) return false;
      const info = blockFromError(e);
      if (info) setBlocked(info);
      toast.error(info?.message || e?.response?.data?.detail || "Error enviando el mensaje.", { duration: 10000 });
      if (rollback) setMessages(rollback);
      else setMessages((prev) => (prev[prev.length - 1]?.role === "user" ? prev.slice(0, -1) : prev));
      return false;
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const vote = async (v) => {
    const next = resolved === v ? null : v; // clicking the chosen answer again removes the vote
    try {
      const r = await rateAgentChat(slug, chatId, next);
      setResolved(r.resolved ?? null);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "No se pudo guardar su valoración.");
    }
  };

  const newChat = () => {
    setResolved(null);
    setMessages([]);
    setParams({}, { replace: true });
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.origin + `/agentes/${slug}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("No se pudo copiar el enlace.");
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-950 text-slate-300">
        <Loader2 className="mr-3 h-5 w-5 animate-spin" />
        <span className="text-sm uppercase tracking-[0.22em]">Cargando agente...</span>
      </div>
    );
  }
  if (gate) {
    if (gate.active === false) {
      return <AgentInactiveCard agent={gate} message={blocked?.message || gate.inactive_message || BLOCK_MESSAGES.company_inactive} />;
    }
    if (gate.access_code === "not_member") {
      return (
        <AgentNotMemberCard
          agent={gate}
          canSwitch={!!localStorage.getItem(accessFlagKey(gate.company_slug))}
          onSwitched={reloadAgent}
        />
      );
    }
    return (
      <AgentAccessGate
        agent={gate}
        onGranted={() => reloadAgent()}
        onInactive={() => {
          setBlocked({ code: "company_inactive", message: BLOCK_MESSAGES.company_inactive });
          setGate({ ...gate, active: false, inactive_reason: "company_inactive" });
        }}
      />
    );
  }
  if (!agent) {
    return (
      <div className="flex h-screen flex-col items-center justify-center bg-slate-950 text-slate-300">
        <p className="font-display text-2xl text-slate-100">Agente no encontrado</p>
        <Button onClick={() => navigate("/")} className="mt-6 rounded-sm bg-slate-100 text-slate-950 hover:bg-white">
          Volver al inicio
        </Button>
      </div>
    );
  }

  const enabledTools = (agent.tools || []).filter((t) => t.enabled);
  const empty = messages.length === 0;
  const locked = !!blocked && STATIC_BLOCKS.includes(blocked.code);

  return (
    <div className="flex h-screen w-screen overflow-hidden bg-slate-950 text-slate-100" data-testid="agent-chat-page">
      {/* SIDEBAR */}
      <aside className="hidden h-full w-[300px] shrink-0 flex-col border-r border-slate-800 bg-slate-950/80 lg:flex">
        <div className="px-6 pt-7">
          <button
            onClick={() => navigate("/")}
            className="text-[11px] uppercase tracking-[0.22em] text-slate-500 hover:text-slate-300"
          >
            Inviktus
          </button>
          <h1 className="mt-3 font-display text-2xl font-medium leading-tight text-slate-50">
            {agent.name}
          </h1>
          <p className="mt-1 text-xs uppercase tracking-[0.18em] text-accent-blue-light">
            {agent.department}
          </p>
        </div>

        <div className="mt-5 border-t border-slate-800 px-6 py-5">
          <Button
            onClick={newChat}
            variant="outline"
            className="w-full justify-start rounded-sm border-slate-700 bg-slate-900/60 text-sm font-medium text-slate-100 hover:bg-slate-800"
            data-testid="new-chat-btn"
          >
            <MessageSquarePlus className="mr-2 h-4 w-4" /> Nuevo chat
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto thin-scroll px-6 pb-4">
          <p className="eyebrow text-slate-500">Iniciadores</p>
          <div className="mt-3 space-y-2" data-testid="conversation-starters">
            {(agent.prompts || []).map((p) => (
              <button
                key={p.id}
                onClick={() => send(p.prompt_text, p.id)}
                disabled={sending || locked}
                className="group w-full border border-slate-800 bg-slate-900/40 px-3 py-2.5 text-left transition hover:border-teal-600/60 hover:bg-slate-800/60 disabled:opacity-50"
                data-testid={`starter-${p.id}`}
              >
                <p className="line-clamp-2 text-[13px] font-medium leading-snug text-slate-100">
                  {p.title}
                </p>
                {p.activity_ref && (
                  <p className="mt-1 truncate text-[10px] uppercase tracking-[0.18em] text-slate-500">
                    {p.activity_ref}
                  </p>
                )}
              </button>
            ))}
            {(agent.prompts || []).length === 0 && (
              <p className="text-sm italic text-slate-500">Sin iniciadores. Edite el agente para añadir.</p>
            )}
          </div>
        </div>

        <div className="border-t border-slate-800 px-6 py-4">
          <p className="eyebrow text-slate-500 flex items-center gap-2">
            <Plug className="h-3 w-3" /> Herramientas (MCP)
          </p>
          <div className="mt-3 flex flex-wrap gap-1.5" data-testid="tools-pills">
            {enabledTools.length === 0 ? (
              <span className="text-[11px] italic text-slate-500">Sin herramientas conectadas.</span>
            ) : (
              enabledTools.map((t) => (
                <span
                  key={t.id}
                  className="border border-teal-700/40 bg-teal-900/20 px-2 py-0.5 text-[10px] uppercase tracking-[0.12em] text-accent-blue-light"
                  title={t.mcp}
                >
                  {t.label}
                </span>
              ))
            )}
          </div>
          <p className="mt-3 text-[10px] uppercase tracking-[0.18em] text-amber-400/80" data-testid="mcp-mocked-notice">
            MCP MOCKED · Prototipo
          </p>
        </div>

        {collab && (
          <div className="border-t border-slate-800 px-6 py-4" data-testid="collaborator-session">
            <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Sesión de colaborador</p>
            <p className="mt-1 truncate text-[13px] text-slate-200" data-testid="collaborator-email">{collab}</p>
            <Button
              onClick={logoutCollaborator}
              variant="outline"
              className="mt-3 w-full justify-start rounded-sm border-slate-700 bg-transparent text-sm text-slate-100 hover:bg-slate-800"
              data-testid="access-logout-btn"
            >
              <LogOut className="mr-2 h-4 w-4" /> Cerrar sesión
            </Button>
          </div>
        )}

        <div className="border-t border-slate-800 px-6 py-4">
          <div className="flex gap-2">
            {!collab && isStaff && <Button
              onClick={() => navigate(`/agentes/${slug}/editar`)}
              variant="outline"
              className="flex-1 rounded-sm border-slate-700 bg-transparent text-[11px] uppercase tracking-[0.18em] text-slate-200 hover:bg-slate-800"
              data-testid="edit-agent-btn"
            >
              <Settings2 className="mr-1.5 h-3.5 w-3.5" /> Editar
            </Button>}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    onClick={copyLink}
                    variant="outline"
                    className="rounded-sm border-slate-700 bg-transparent text-slate-200 hover:bg-slate-800"
                    data-testid="copy-link-btn"
                  >
                    {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Copiar enlace público</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <ThemeToggle />
          </div>
        </div>
      </aside>

      {/* MAIN CHAT */}
      <main className="flex h-full min-w-0 flex-1 flex-col bg-slate-950">
        <header className="flex items-center justify-between gap-4 border-b border-slate-800 px-6 py-4 lg:px-10">
          <div className="lg:hidden">
            <p className="text-[10px] uppercase tracking-[0.22em] text-accent-blue-light">{agent.department}</p>
            <p className="font-display text-base font-medium text-slate-50">{agent.name}</p>
          </div>
          <div className="hidden min-w-0 items-start gap-3 lg:flex" data-testid="agent-chat-header-title">
            <Sparkles className="mt-1 h-4 w-4 shrink-0 text-teal-400" />
            <div className="min-w-0">
              <p className="font-display text-base font-medium text-slate-50">{agent.name}</p>
              <p className="mt-0.5 text-[13px] leading-snug text-slate-400" data-testid="agent-chat-header-description">
                {agent.description || "Asistente IA"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {collab ? (
              <button
                onClick={logoutCollaborator}
                className="text-xs uppercase tracking-[0.2em] text-slate-400 hover:text-slate-100 lg:hidden"
                data-testid="access-logout-btn-mobile"
              >
                Cerrar sesión
              </button>
            ) : isStaff && (
              <a
                href={`/agentes/${slug}/editar`}
                className="text-xs uppercase tracking-[0.2em] text-slate-400 hover:text-slate-100 lg:hidden"
                data-testid="edit-agent-link-mobile"
              >
                Editar
              </a>
            )}
          </div>
        </header>

        {empty ? (
          /* Empty state with starters in main area */
          <div className="flex flex-1 items-start justify-center overflow-y-auto thin-scroll px-6 py-12 lg:px-16">
            <div className="w-full max-w-3xl text-center">
              <div className="mx-auto inline-flex items-center justify-center border border-teal-600/40 bg-teal-900/20 px-3 py-1 text-[10px] uppercase tracking-[0.22em] text-accent-blue-light">
                {agent.department}
              </div>
              <h2 className="mt-6 font-display text-4xl font-medium text-slate-50">{agent.name}</h2>
              <p className="mx-auto mt-4 max-w-xl whitespace-pre-wrap text-sm leading-relaxed text-slate-300">
                {agent.description}
              </p>

              <div className="mt-12 grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="starters-grid">
                {(agent.prompts || []).map((p) => (
                  <button
                    key={p.id}
                    onClick={() => send(p.prompt_text, p.id)}
                    disabled={sending || locked}
                    className="group flex h-full flex-col items-start border border-slate-800 bg-slate-900/50 p-5 text-left transition hover:border-teal-500/60 hover:bg-slate-800/60 disabled:opacity-50"
                    data-testid={`starter-grid-${p.id}`}
                  >
                    <p className="font-display text-base font-medium leading-snug text-slate-100">
                      {p.title}
                    </p>
                    {p.activity_ref && (
                      <p className="mt-2 text-[10px] uppercase tracking-[0.18em] text-slate-500">
                        {p.activity_ref}
                      </p>
                    )}
                  </button>
                ))}
                {(agent.prompts || []).length === 0 && (
                  <p className="col-span-2 italic text-slate-500">
                    Este agente todavía no tiene iniciadores configurados.
                  </p>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto thin-scroll px-6 py-10 lg:px-16"
            data-testid="agent-messages"
          >
            <div className="mx-auto max-w-3xl space-y-8">
              {messages.map((m, i) => (
                <AgentMessage key={i} message={m} agentName={agent.name} />
              ))}
              {chatId && !sending && messages.length > 0 && messages[messages.length - 1].role === "agent" && (
                <div className="flex flex-wrap items-center gap-2 pl-12 text-xs text-slate-400" data-testid="chat-resolved">
                  <span>¿Le resolvió esto?</span>
                  {[[true, "Sí"], [false, "No"]].map(([v, label]) => (
                    <button
                      key={label}
                      type="button"
                      onClick={() => vote(v)}
                      aria-pressed={resolved === v}
                      className={`border px-2.5 py-0.5 transition ${resolved === v
                        ? "border-teal-500 bg-teal-600/20 text-accent-blue-light"
                        : "border-slate-600 hover:border-slate-400"}`}
                      data-testid={`chat-resolved-${v ? "yes" : "no"}`}
                    >
                      {label}
                    </button>
                  ))}
                  {resolved !== null && <span className="text-slate-500">Gracias. Puede cambiar su respuesta.</span>}
                </div>
              )}
              {sending && (
                <div className="flex gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center bg-teal-600/20 text-accent-blue-light">
                    <Sparkles className="h-4 w-4" />
                  </div>
                  <div className="flex items-center gap-1 pt-3">
                    <span className="typing-dot"></span>
                    <span className="typing-dot"></span>
                    <span className="typing-dot"></span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Composer */}
        <div className="border-t border-slate-800 bg-slate-950 px-6 py-5 lg:px-16">
          <div className="mx-auto max-w-3xl">
            {blocked && (
              <div
                className="mb-3 flex items-start gap-2 border border-amber-400 bg-amber-50 px-4 py-3 text-[13px] leading-relaxed text-amber-900"
                data-testid="agent-blocked-notice"
                data-code={blocked.code}
              >
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
                <span>{blocked.message}</span>
              </div>
            )}
            <div className="flex items-end gap-2 border border-slate-800 bg-slate-900/60 focus-within:border-teal-600">
              <Textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKey}
                placeholder='Escriba aquí... · O pulse un iniciador del menú'
                disabled={sending || locked}
                rows={2}
                className="min-h-[60px] resize-none border-0 bg-transparent px-4 py-3.5 text-[15px] leading-relaxed text-slate-100 placeholder:text-slate-500 focus-visible:ring-0 focus-visible:ring-offset-0"
                data-testid="agent-chat-input"
              />
              <Button
                onClick={() => send()}
                disabled={!input.trim() || sending || locked}
                className="m-2 h-10 w-10 shrink-0 rounded-sm bg-teal-600 p-0 text-white hover:bg-teal-500 disabled:bg-slate-800 disabled:text-slate-600"
                aria-label="Enviar"
                data-testid="agent-send-btn"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUp className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

function AgentMessage({ message, agentName }) {
  if (message.role === "agent") {
    const citations = message.citations || [];
    const truncated = !!message.truncated_by_limit;
    const content = truncated ? (message.content || "").replace(TRUNCATION_RE, "") : message.content;
    return (
      <div className="flex gap-4" data-testid="agent-msg">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center bg-teal-600/20 text-accent-blue-light">
          <Sparkles className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] uppercase tracking-[0.2em] text-accent-blue-light">{agentName}</p>
          <div className="mt-2 text-[15px] leading-[1.75] text-slate-100" data-testid="agent-msg-content">
            <MarkdownMessage>{content}</MarkdownMessage>
          </div>
          {truncated && (
            <p
              className="mt-3 flex items-start gap-2 border-l-2 border-amber-500 bg-amber-50 px-3 py-2 text-[12px] leading-relaxed text-amber-800"
              data-testid="agent-msg-truncated-note"
            >
              <Scissors className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              Respuesta acortada: se alcanzó el límite mensual de tokens autorizado para este agente. Contacte al
              administrador de su empresa para ampliarlo.
            </p>
          )}
          {message.rag_warning && (
            <p className="mt-3 text-[11px] text-amber-300/90" data-testid="agent-rag-warning">
              ⚠ {message.rag_warning}
            </p>
          )}
          {citations.length > 0 && (
            <div className="mt-4 border-l-2 border-teal-700/50 pl-3" data-testid="agent-citations">
              <p className="text-[10px] uppercase tracking-[0.22em] text-accent-blue-light/80">
                Fuentes del Knowledge Base ({citations.length})
              </p>
              <div className="mt-2 space-y-1.5">
                {citations.map((c, i) => (
                  <details
                    key={`${c.filename}-${c.chunk_index}-${i}`}
                    className="border border-slate-800 bg-slate-900/40 px-3 py-1.5"
                    data-testid={`agent-citation-${i}`}
                  >
                    <summary className="flex cursor-pointer flex-wrap items-center justify-between gap-2 text-[11px] text-slate-300">
                      <span className="font-medium">[{i + 1}] {c.filename}</span>
                      <span className="flex items-center gap-2 font-mono text-[10px] text-slate-500">
                        <span className="border border-teal-700/40 bg-teal-900/20 px-1.5 py-0.5 text-[9px] uppercase tracking-[0.1em] text-accent-blue-light">
                          {c.department}
                        </span>
                        frag #{c.chunk_index} · score {Number(c.score || 0).toFixed(3)}
                      </span>
                    </summary>
                    <p className="mt-2 whitespace-pre-wrap text-[11px] leading-relaxed text-slate-400">
                      {c.snippet}{c.snippet?.length >= 280 ? "…" : ""}
                    </p>
                  </details>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
  return (
    <div className="flex justify-end" data-testid="user-msg">
      <div className="max-w-[80%] border border-slate-700 bg-slate-900 px-4 py-3">
        <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Usted</p>
        <p className="mt-1 whitespace-pre-wrap text-[15px] leading-relaxed text-slate-100">
          {message.content}
        </p>
      </div>
    </div>
  );
}
